import React, { useCallback, useEffect, useState } from 'react';
import AsyncSelect from 'react-select/async';
import { loadActivityCityLocations, type ActivityCityOption } from '../../api/Location';
import {
  fetchActivityOptionSearch,
  searchActivityTours,
  startActivityOptionSearch,
  type ActivityOptionSearchResponse,
  type ActivityTourSearchItem,
  type ActivityTransferQuote,
} from '../../api/Activities';

export type ActivityOptionSelection = {
  activityRef: string;
  tourId: number;
  contractId: number;
  tourName: string;
  tourOptionId: number;
  optionName: string;
  transfer: ActivityTransferQuote;
  travelDate: string;
  travellers: { adults: number; children: number; infants: number };
  quotedAt?: string;
  imageUrl: string | null;
  description: string | null;
};

type Props = {
  defaultTravelDate: string;
  defaultCityName?: string;
  defaultTravellers?: { adults: number; children: number; infants: number };
  onSelect: (selection: ActivityOptionSelection) => void;
  onClose: () => void;
};

const money = (amount: number, currency = 'INR') => new Intl.NumberFormat('en-IN', {
  style: 'currency', currency, maximumFractionDigits: 0,
}).format(amount || 0);

export default function ActivityTourOptionPicker({
  defaultTravelDate,
  defaultCityName,
  defaultTravellers = { adults: 2, children: 0, infants: 0 },
  onSelect,
  onClose,
}: Props) {
  const [city, setCity] = useState<ActivityCityOption | null>(null);
  const [travelDate, setTravelDate] = useState(defaultTravelDate);
  const [adults, setAdults] = useState(defaultTravellers.adults);
  const [children, setChildren] = useState(defaultTravellers.children);
  const [infants, setInfants] = useState(defaultTravellers.infants);
  const [activityName, setActivityName] = useState('');
  const [tours, setTours] = useState<ActivityTourSearchItem[]>([]);
  const [tourPagination, setTourPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });
  const [selectedTour, setSelectedTour] = useState<ActivityTourSearchItem | null>(null);
  const [optionSearch, setOptionSearch] = useState<ActivityOptionSearchResponse | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState('');
  const [autoSearchWhenReady, setAutoSearchWhenReady] = useState(Boolean(defaultCityName));

  const travellers = { adults, children, infants };

  useEffect(() => {
    if (!optionSearch?.jobId || optionSearch.status !== 'PENDING') return;
    const timer = window.setTimeout(() => {
      void fetchActivityOptionSearch(optionSearch.jobId)
        .then(setOptionSearch)
        .catch((pollError: Error) => setOptionSearch((current) => current ? { ...current, status: 'FAILED', error: pollError.message } : current));
    }, 800);
    return () => window.clearTimeout(timer);
  }, [optionSearch]);

  const searchTours = useCallback(async (selectedCity = city, page = 1) => {
    if (!selectedCity) {
      setError('Select an activity city first.');
      return;
    }
    setIsSearching(true);
    setError('');
    setSelectedTour(null);
    setOptionSearch(null);
    try {
      const response = await searchActivityTours({
        cityId: selectedCity.cityId,
        name: activityName,
        travelDate,
        travellers: { adults, children, infants },
        page,
        limit: 20,
      });
      setTours(response.items);
      setTourPagination(response.pagination);
      if (response.previewError) setError('Tour list loaded, but live preview prices are temporarily unavailable.');
    } catch (searchError: any) {
      setTours([]);
      setTourPagination({ page: 1, limit: 20, total: 0, totalPages: 1 });
      setError(searchError?.message || 'Unable to load tours.');
    } finally {
      setIsSearching(false);
    }
  }, [activityName, adults, children, city, infants, travelDate]);

  useEffect(() => {
    const requestedCity = defaultCityName?.trim();
    if (!requestedCity) return;
    let cancelled = false;
    void loadActivityCityLocations(requestedCity)
      .then((matches) => {
        if (cancelled) return;
        const normalized = requestedCity.toLocaleLowerCase();
        const match = matches.find((item) => item.label.toLocaleLowerCase() === normalized)
          || matches.find((item) => item.label.toLocaleLowerCase().startsWith(`${normalized},`))
          || matches[0];
        if (!match) {
          setAutoSearchWhenReady(false);
          setError(`No Rayna activity city was found for ${requestedCity}. Select a city to continue.`);
          return;
        }
        setCity(match);
      })
      .catch(() => {
        if (!cancelled) {
          setAutoSearchWhenReady(false);
          setError(`Unable to load the Rayna activity city for ${requestedCity}.`);
        }
      });
    return () => { cancelled = true; };
  }, [defaultCityName]);

  useEffect(() => {
    if (!autoSearchWhenReady || !city) return;
    setAutoSearchWhenReady(false);
    void searchTours(city, 1);
  }, [autoSearchWhenReady, city, searchTours]);

  const selectTour = async (tour: ActivityTourSearchItem) => {
    setSelectedTour(tour);
    setOptionSearch(null);
    setError('');
    try {
      setOptionSearch(await startActivityOptionSearch({ activityRef: tour.activityRef, contractId: tour.contractId, travelDate, travellers }));
    } catch (optionError: any) {
      setError(optionError?.message || 'Unable to load tour options.');
    }
  };

  const renderSelectedTourOptions = (tour: ActivityTourSearchItem) => {
    if (selectedTour?.activityRef !== tour.activityRef) return null;
    return <section className="rayna-live-options" aria-live="polite">
      <div className="rayna-live-options__header">
        <div><span className="rayna-live-options__eyebrow">Live options</span><strong>Choose a transfer</strong></div>
        <span className="rayna-live-options__travellers">{adults} adult{adults === 1 ? '' : 's'}{children ? ` · ${children} child${children === 1 ? '' : 'ren'}` : ''}{infants ? ` · ${infants} infant${infants === 1 ? '' : 's'}` : ''}</span>
      </div>
      {optionSearch?.status === 'PENDING' && <div className="rayna-live-options__loading">Checking live transfers and prices…</div>}
      {optionSearch?.status === 'FAILED' && <div className="quotation-warning">{optionSearch.error || 'Live option pricing is unavailable.'}</div>}
      {optionSearch?.status === 'SUCCESS' && optionSearch.options.map((option) => (
        <div key={option.tourOptionId} className="rayna-live-options__option">
          <div className="rayna-live-options__option-header"><strong>{option.optionName}</strong><span>{option.transfers.length} transfer{option.transfers.length === 1 ? '' : 's'}</span></div>
          {option.status === 'UNAVAILABLE' ? <p className="guided-package-builder__section-subtitle">Not available for this date and traveller mix.</p> : <div className="package-editor-addon-list" style={{ marginTop: 10 }}>
            {option.transfers.map((transfer) => <button type="button" className="rayna-live-options__transfer" key={`${option.tourOptionId}-${transfer.transferId}`} onClick={() => onSelect({ activityRef: tour.activityRef, tourId: tour.tourId, contractId: tour.contractId, tourName: tour.name, tourOptionId: option.tourOptionId, optionName: option.optionName, transfer, travelDate, travellers, quotedAt: optionSearch.quotedAt, imageUrl: tour.imageUrl, description: tour.description })}>
              <span className="rayna-live-options__transfer-copy"><strong>{transfer.transferName}</strong><span>{transfer.startTime || 'Time confirmed during booking'}{transfer.isSlot ? ' · Slot required' : ''}</span></span>
              <span className="rayna-live-options__transfer-price"><strong>{money(transfer.totalPrice, transfer.currency)}</strong><span>Total</span></span>
            </button>)}
          </div>}
        </div>
      ))}
    </section>;
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal rayna-activity-picker" onClick={(event) => event.stopPropagation()}>
        <button className="close-btn" onClick={onClose} aria-label="Close">×</button>
        <h2>Choose a live tour option</h2>
        <p className="guided-package-builder__section-subtitle">The itinerary city, travel date and traveller mix are prefilled. Live transfer options and prices are loaded only for the tour you open.</p>

        <div className="guided-package-builder__grid" style={{ marginTop: 18 }}>
          <label><span>Activity city</span><AsyncSelect<ActivityCityOption, false>
            cacheOptions defaultOptions loadOptions={loadActivityCityLocations} value={city}
            onChange={(value) => { setCity(value); setTours([]); setSelectedTour(null); setOptionSearch(null); setTourPagination({ page: 1, limit: 20, total: 0, totalPages: 1 }); }} placeholder="Search city" classNamePrefix="package-editor-select"
          /></label>
          <label><span>Travel date</span><input className="package-editor-input" type="date" value={travelDate} min={new Date().toISOString().slice(0, 10)} onChange={(event) => setTravelDate(event.target.value)} /></label>
          <label><span>Adults</span><input className="package-editor-input" type="number" min={1} max={12} value={adults} onChange={(event) => setAdults(Math.max(1, Number(event.target.value || 1)))} /></label>
          <label><span>Children</span><input className="package-editor-input" type="number" min={0} max={12} value={children} onChange={(event) => setChildren(Math.max(0, Number(event.target.value || 0)))} /></label>
          <label><span>Infants</span><input className="package-editor-input" type="number" min={0} max={12} value={infants} onChange={(event) => setInfants(Math.max(0, Number(event.target.value || 0)))} /></label>
          <label><span>Activity name <em>(optional)</em></span><input className="package-editor-input" type="search" value={activityName} placeholder="e.g. Dhow Cruise Dinner" onChange={(event) => setActivityName(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); void searchTours(city, 1); } }} /></label>
        </div>
        <button type="button" className="package-editor-btn-primary" style={{ marginTop: 14 }} disabled={isSearching} onClick={() => void searchTours()}>{isSearching ? 'Loading tours…' : 'Search tours'}</button>
        {error && <div className="quotation-warning" style={{ marginTop: 14 }}>{error}</div>}

        <div className="package-editor-addon-list" style={{ marginTop: 18 }}>
          {tours.map((tour) => <React.Fragment key={tour.activityRef}>
            <button type="button" className={`package-editor-addon-card rayna-tour-card${selectedTour?.activityRef === tour.activityRef ? ' rayna-tour-card--selected' : ''}`} aria-expanded={selectedTour?.activityRef === tour.activityRef} onClick={() => void selectTour(tour)} style={{ display: 'grid', gridTemplateColumns: tour.imageUrl ? '86px minmax(0, 1fr) auto' : 'minmax(0, 1fr) auto', gap: 14, textAlign: 'left' }}>
              {tour.imageUrl && <img src={tour.imageUrl} alt="" style={{ width: 86, height: 68, borderRadius: 8, objectFit: 'cover' }} />}
              <span><strong>{tour.name}</strong><span className="guided-package-builder__activity-source">{tour.optionCount} option{tour.optionCount === 1 ? '' : 's'}</span><span style={{ display: 'block', marginTop: 4 }}>{tour.duration || 'Tour details available after selection'}</span></span>
              <strong style={{ whiteSpace: 'nowrap' }}>{tour.pricePreview ? `From ${money(tour.pricePreview.amount, tour.pricePreview.currency)}` : 'Price on request'}</strong>
            </button>
            {renderSelectedTourOptions(tour)}
          </React.Fragment>)}
        </div>
        {tourPagination.totalPages > 1 && <div className="guided-package-builder__section-head" style={{ marginTop: 14 }}>
          <span className="guided-package-builder__section-subtitle">Showing {((tourPagination.page - 1) * tourPagination.limit) + 1}–{Math.min(tourPagination.page * tourPagination.limit, tourPagination.total)} of {tourPagination.total} tours</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="package-editor-btn-secondary" disabled={isSearching || tourPagination.page <= 1} onClick={() => void searchTours(city, tourPagination.page - 1)}>Previous</button>
            <button type="button" className="package-editor-btn-secondary" disabled={isSearching || tourPagination.page >= tourPagination.totalPages} onClick={() => void searchTours(city, tourPagination.page + 1)}>Next</button>
          </div>
        </div>}
      </div>
    </div>
  );
}
